/**
 * NumberLine.js
 * A visual number line entity that can be used between UI elements
 */
export default class NumberLine {
    /**
     * Create a new number line
     * @param {Object} options - Configuration options
     * @param {number} options.markerCount - Number of markers to display (default: 3)
     * @param {Array<number>} options.labelPositions - Array of positions (0-based indices) where labels should be shown
     * @param {Function} options.labelFormatter - Function to format label text (receives index, returns string)
     * @param {string} options.width - CSS width value (default: '150px')
     */
    constructor(options = {}) {
        this.options = {
            markerCount: options.markerCount || 3,
            labelPositions: options.labelPositions || [1],
            labelFormatter: options.labelFormatter || (index => '0'),
            width: options.width || '150px'
        };
        
        this.element = this.createNumberLine();
    }
    
    /**
     * Create the number line DOM element
     * @returns {HTMLElement} The number line element
     */
    createNumberLine() {
        // Create the number line container
        const numberLine = document.createElement('div');
        numberLine.className = 'number-line';
        numberLine.style.width = this.options.width;
        
        // Create markers for the number line
        for (let i = 0; i < this.options.markerCount; i++) {
            const marker = document.createElement('div');
            marker.className = 'number-marker';
            
            // Add number labels at specified positions
            if (this.options.labelPositions.includes(i)) {
                const label = document.createElement('div');
                label.className = 'number-label';
                label.textContent = this.options.labelFormatter(i);
                marker.appendChild(label);
            }
            
            // Set the position along the line
            marker.style.left = `${(i / (this.options.markerCount - 1)) * 100}%`;
            numberLine.appendChild(marker);
        }
        
        return numberLine;
    }
    
    /**
     * Get the DOM element for the number line
     * @returns {HTMLElement} The number line element
     */
    getElement() {
        return this.element;
    }
    
    /**
     * Update the number line options
     * @param {Object} options - New options to apply
     */
    updateOptions(options) {
        // Update options
        this.options = {
            ...this.options,
            ...options
        };
        
        // Replace the element with a new one using updated options
        const parent = this.element.parentNode;
        if (parent) {
            const newElement = this.createNumberLine();
            parent.replaceChild(newElement, this.element);
            this.element = newElement;
        } else {
            this.element = this.createNumberLine();
        }
    }
}
